import { cn } from "@/lib/utils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, ChevronLeft, ChevronRight } from "lucide-react";

interface HotInfoTip {
  title: string;
  subtitle: string;
  details: { label: string; text: string }[];
}

const HOT_INFO_LABEL = "CO TERAZ DZIAŁA";
const HOT_INFO_PILL = "Na czasie";

const TIPS: HotInfoTip[] = [
  {
    title: "Karuzele = więcej zasięgu",
    subtitle: "Instagram płaci za czas spędzony na ekranie",
    details: [
      {
        label: "Zasięg x2",
        text: "Karuzele działają jak album ze zdjęciam — każdy slajd buduje ciekawość. Użytkownik przewija, zostaje dłużej, a algorytm to widzi i podbija zasięg. Do tego Instagram pokazuje karuzelę w feedzie kilka razy — jeśli pierwszy slajd nie zatrzymał, drugi lub trzeci może to zrobić za ciebie",
      },
      {
        label: "Jak to zrobić",
        text: "6–10 slajdów. Pierwszy zatrzymuje. Każdy kolejny dostarcza wartość.",
      },
      {
        label: "Zrób to teraz",
        text: "Masz już temat w bibliotece. Zrób z niego karuzelę dziś.",
      },
    ],
  },
  {
    title: "Napisy = więcej osób ogląda do końca",
    subtitle: "Wiele osób ogląda Instagram bez dźwięku. Jeśli nie ma tekstu na ekranie — nie wiedzą, o czym mówisz.",
    details: [
      {
        label: "Dlaczego to działa",
        text: "Duża część ludzi scrolluje w pracy, w autobusie albo przy kimś obok. Bez audio. Gdy nie widzą napisów, po 2–3 sekundach przewijają dalej. Napisy pomagają zrozumieć przekaz i sprawiają, że widz zostaje dłużej. A im dłuższe oglądanie, tym większy zasięg daje Instagram.",
      },
      {
        label: "Jak to zrobić dobrze",
        text: "Dodaj duże, czytelne napisy. Krótkie zdania. Zmieniaj tekst co kilka sekund. Wyróżnij najważniejsze słowa.",
      },
      {
        label: "Zrób to dziś",
        text: "Następną rolkę dodaj z napisami. Potem porównaj średnią długość oglądania z poprzednią publikacją.",
      },
    ],
  },
  {
    title: "Nagrywaj kilka hooków",
    subtitle: "Pierwsze 2 sekundy decydują, czy ktoś zostanie. Jeden wstęp to za mało.",
    details: [
      {
        label: "Dlaczego to ważne",
        text: "Hook to moment, który zatrzymuje scroll. Jeśli początek jest słaby, nawet dobre wideo nie dostanie zasięgu. Nagrywając 3 różne otwarcia, zwiększasz szansę, że jedno z nich złapie uwagę i podniesie retencję.",
      },
      {
        label: "Jak to zrobić",
        text: "Podczas nagrania zrób 3 wersje pierwszego zdania. Podczas montażu przygotuj 3 pliki z różnymi hookami. Już wtedy zobaczysz, który brzmi najmocniej.",
      },
      {
        label: "Zrób to dziś",
        text: "Wrzucasz rolkę i nie idzie? Opublikuj ją ponownie z innym hookiem i porównaj wyniki.",
      },
    ],
  },
  {
    title: "Rozbudowane opisy pod rolką",
    subtitle: 'Opis to nie tylko miejsce na emoji. To wpływa na wyszukiwanie w "lupce".',
    details: [
      {
        label: "Dlaczego to działa",
        text: 'Instagram coraz mocniej pokazuje rolki z wyszukiwarki. Jeśli w opisie użyjesz konkretnych słów, które ludzie wpisują (np. "fryzura przy zakolach", "jak stylizować włosy"), Twoja rolka ma szansę pojawić się nowym osobom. Szczególnie wtedy, gdy rozwiązujesz realny problem.',
      },
      {
        label: "Jak to zrobić",
        text: "Dodaj 3–5 zdań wyjaśnienia. Używaj prostych fraz, które ktoś może wpisać w wyszukiwarce. Pisz naturalnie, nie listę hashtagów.",
      },
      {
        label: "Zrób to dziś",
        text: "Do następnej rolki dodaj dłuższy, konkretny opis. Sprawdź, czy pojawią się wejścia z wyszukiwania.",
      },
    ],
  },
  {
    title: "Powtarzalny format",
    subtitle: "Znajoma twarz. Znajome miejsce. Znajomy klimat.",
    details: [
      {
        label: "Dlaczego to działa",
        text: 'Ludzie szybciej reagują na coś, co już znają. Jeśli nagrywasz w podobnym kadrze, z tą samą energią i stylem, widz podświadomie rozpoznaje: "to on". Nie musi się zastanawiać, kto mówi — zostaje dłużej. To może zmniejszyć liczbę pominięć i poprawić pierwsze sekundy retencji.',
      },
      {
        label: "Jak to zrobić",
        text: "Wybierz 1–2 stałe miejsca do nagrań. Utrzymuj podobny kadr i światło. Nie zmieniaj stylu w każdej rolce.",
      },
      {
        label: "Zrób to dziś",
        text: "Przez 10 kolejnych rolek trzymaj ten sam klimat. Sprawdź, czy spada liczba szybkich przewinięć.",
      },
    ],
  },
  {
    title: "Rachunek sumienia przed publikacją",
    subtitle: "Zadaj sobie jedno pytanie: czy sam obejrzałbyś tę rolkę do końca?",
    details: [
      {
        label: "Dlaczego to ważne",
        text: "Czasem nagrywamy materiał, w który sami nie wierzymy. Jest poprawny, ale bez energii i przekonania. Widz to czuje w pierwszych sekundach. Efekt? Średnia retencja i przeciętne zasięgi. Jeśli Ty się nudzisz — odbiorca też się znudzi.",
      },
      {
        label: "Co zrobić",
        text: 'Przed publikacją obejrzyj rolkę jak zwykły widz. Bez sentymentu do pracy włożonej w montaż. Jeśli czujesz, że "mogłoby być lepiej" — nagraj jeszcze raz.',
      },
      {
        label: "Zrób to dziś",
        text: "Masz gotowy materiał, ale nie czujesz go w 100%? Możesz go wrzucić. Ale następną rolkę nagraj lepiej — albo o czymś, w co naprawdę wierzysz.",
      },
    ],
  },
  {
    title: "Dodawaj ciekawostki",
    subtitle: "Nie tylko opinia. Daj widzowi coś, czego nie wiedział.",
    details: [
      {
        label: "Dlaczego to działa",
        text: "Ludzie cenią konkrety. Jeśli w rolce pojawia się nieoczywista wiedza, ciekawy tip albo dobra rada — rośnie wartość materiału. Widz ma poczucie, że czegoś się nauczył. To zwiększa szansę na zapis, udostępnienie i powrót na profil.",
      },
      {
        label: "Jak to zrobić",
        text: "Przeczytaj ciekawostki, które masz w scenariuszu. Wybierz 1–2 najlepiej pasujące do tematu. Wpleć je naturalnie w materiał, zamiast mówić ogólniki.",
      },
      {
        label: "Zrób to dziś",
        text: "W kolejnej rolce użyj jednej nieoczywistej informacji. Pokaż, że poza opinią dajesz realną wartość.",
      },
    ],
  },
  {
    title: "Recykling treści",
    subtitle: "Możesz dodawać te same rolki ponownie — bez kary zasięgu.",
    details: [
      {
        label: "Dlaczego to działa",
        text: "Instagram potwierdził, że ponowna publikacja nie obcina zasięgów. Większość Twoich obserwatorów i tak nie widzi każdej rolki. Dlatego materiał, który dobrze performował, może ponownie trafić do nowych osób i wygenerować kolejne wyświetlenia.",
      },
      {
        label: "Jak to zrobić",
        text: "Wybierz rolki z najlepszą retencją i zapisami. Odczekaj kilka tygodni. Opublikuj je ponownie — możesz zmienić hook lub opis.",
      },
      {
        label: "Zrób to dziś",
        text: "Sprawdź swoje top 5 rolek. Jedną z nich zaplanuj do ponownej publikacji w najbliższym czasie.",
      },
    ],
  },
  {
    title: "Poznaj swojego odbiorcę",
    subtitle: "Musisz wiedzieć, do kogo mówisz.",
    details: [
      {
        label: "Dlaczego to ważne",
        text: "Jeśli nie wiesz, kim jest Twój widz, mówisz do wszystkich — czyli do nikogo. Gdy masz w głowie konkretną osobę, łatwiej dobrać język, przykłady i ton. Materiał staje się naturalny. Twoja energia jest lepsza, a przekaz bardziej zrozumiały.",
      },
      {
        label: "Jak to zrobić",
        text: "Określ, kto ogląda Twoje treści. Ile ma lat? Z jakim problemem przychodzi? Podczas nagrania wyobraź sobie, że mówisz do jednej osoby — kolegi, koleżanki, wujka.",
      },
      {
        label: "Zrób to dziś",
        text: "Przed kolejną rolką zapisz, do kogo ją kierujesz. Nagraj materiał tak, jakbyś mówił tylko do tej osoby.",
      },
    ],
  },
  {
    title: "Ilość ponad jakość (na start)",
    subtitle: "Na początku liczy się częstotliwość, nie perfekcja.",
    details: [
      {
        label: "Dlaczego to działa",
        text: 'Czekanie na "idealny" materiał blokuje rozwój. Algorytm uczy się Twoich treści dopiero wtedy, gdy publikujesz regularnie. Im więcej nagrasz, tym szybciej zobaczysz, co działa, a co nie. Doświadczenie i wyczucie przychodzą z liczbą powtórzeń.',
      },
      {
        label: "Jak to robić mądrze",
        text: "Nie obniżaj standardu. Po prostu nie czekaj tygodniami na perfekcję. Publikuj częściej i ucz się na wynikach.",
      },
      {
        label: "Zrób to dziś",
        text: "Zamiast dopieszczać jedną rolkę 3 dni, nagraj 3 w jeden dzień. Sprawdź, która złapie najlepszą retencję.",
      },
    ],
  },
];

const getDailyTipIndex = (): number => {
  const now = new Date();
  const daysSinceEpoch = Math.floor(now.getTime() / (1000 * 60 * 60 * 24));
  return daysSinceEpoch % TIPS.length;
};

export const HotInfoCard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [tipIndex, setTipIndex] = useState(getDailyTipIndex);
  const navigate = useNavigate();

  const currentTip = TIPS[tipIndex];

  const goToPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTipIndex((prev) => (prev - 1 + TIPS.length) % TIPS.length);
  };

  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTipIndex((prev) => (prev + 1) % TIPS.length);
  };

  return (
    <div className="flex items-center justify-center w-full">
      <div className="w-full">
        <div
          className={cn(
            "relative rounded-2xl border-2 bg-card transition-all duration-500 ease-in-out overflow-hidden",
            isOpen
              ? "border-primary shadow-lg shadow-primary/10"
              : "border-primary/30 hover:border-primary/50"
          )}
        >
          {/* Main header area */}
          <div className="relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full px-5 py-4 flex items-center gap-4 cursor-pointer group"
            >
              {/* Clock icon with pulse glow */}
              <div className="relative flex-shrink-0">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{ animation: "hotinfo-pulseGlow 2s ease-in-out infinite" }}
                />
                <div className="relative w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-primary">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
                      fill="currentColor"
                      opacity="0.3"
                    />
                    <path
                      d="M12 6v6l4 2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        strokeDasharray: 1,
                        animation: "hotinfo-slidingLine 4s ease-in-out infinite alternate",
                      }}
                    />
                  </svg>
                </div>
              </div>

              {/* Title section */}
              <div className="flex-1 text-left min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                  {HOT_INFO_LABEL}
                </p>
                <p className="text-base font-bold text-foreground mt-0.5">
                  {currentTip.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                  {currentTip.subtitle}
                </p>
              </div>

              {/* Badge + Chevron */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-bold text-primary-foreground bg-primary"
                  style={{ animation: "hotinfo-bounce 2s ease-in-out infinite" }}
                >
                  🔥 {HOT_INFO_PILL}
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  className={cn(
                    "text-muted-foreground transition-transform duration-300",
                    isOpen && "rotate-180"
                  )}
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>

            {/* Bottom progress line */}
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-muted overflow-hidden">
              <div
                className="h-full bg-primary rounded-full"
                style={{
                  width: isOpen ? "100%" : "0%",
                  transition: "width 1.5s ease-in-out",
                }}
              />
            </div>
          </div>

          {/* Expandable details section */}
          <div
            className={cn(
              "transition-all duration-500 ease-in-out overflow-hidden",
              isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
            )}
          >
            <div className="px-5 pb-5 pt-4">
              <div className="space-y-4">
                {currentTip.details.map((detail, index) => (
                  <div
                    key={`${tipIndex}-${index}`}
                    className="flex gap-3"
                    style={{
                      animation: isOpen
                        ? `hotinfo-slideIn 0.4s ease-out ${index * 0.1}s both`
                        : "none",
                    }}
                  >
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {detail.label}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">
                        {detail.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation + Academy CTA */}
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/akademia');
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors group/cta"
                >
                  <GraduationCap className="h-4 w-4" />
                  <span>Więcej wiedzy w Akademii Viraluj</span>
                  <span className="transition-transform group-hover/cta:translate-x-1">→</span>
                </button>

                {/* Tip navigation */}
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={goToPrev}
                    className="w-7 h-7 rounded-full bg-muted hover:bg-primary/10 flex items-center justify-center transition-colors"
                    aria-label="Poprzednia porada"
                  >
                    <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                  <span className="text-[11px] text-muted-foreground tabular-nums min-w-[2rem] text-center">
                    {tipIndex + 1}/{TIPS.length}
                  </span>
                  <button
                    onClick={goToNext}
                    className="w-7 h-7 rounded-full bg-muted hover:bg-primary/10 flex items-center justify-center transition-colors"
                    aria-label="Następna porada"
                  >
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
